import { useState, useEffect, useRef } from 'react'
import { format, parseISO } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { ApiPlanDetailResponse } from '@/types'

// --- Option types for select fields ---

interface SelectOption {
  value: string
  label: string
}

// --- Field config types ---

type FieldType = 'text' | 'date' | 'float' | 'percent' | 'integer' | 'select'

interface BaseFieldConfig {
  label: string
  key: string
  getValue: (plan: ApiPlanDetailResponse) => string | number | null
  editable: boolean
  type: FieldType
}

interface SelectFieldConfig extends BaseFieldConfig {
  type: 'select'
  options: SelectOption[]
}

type FieldConfig = BaseFieldConfig | SelectFieldConfig

// --- Dropdown option definitions ---

const STRATEGIC_ROLE_OPTIONS: SelectOption[] = [
  { value: 'PRIMARY_BUSINESS_DRIVER', label: 'Primary Business Driver' },
  { value: 'TRAFFIC_DRIVER', label: 'Traffic Driver' },
  { value: 'PROFIT_GENERATOR', label: 'Profit Generator' },
  { value: 'CONVENIENCE', label: 'Convenience' },
  { value: 'EMERGING_BUSINESS', label: 'Emerging Business' },
]

const STRATEGIC_INTENT_OPTIONS: SelectOption[] = [
  { value: 'AGGRESSIVE_GROW', label: 'Aggressive Grow' },
  { value: 'GROW', label: 'Grow' },
  { value: 'MAINTAIN', label: 'Maintain' },
  { value: 'HARVEST_OPTIMIZE', label: 'Harvest Optimize' },
  { value: 'EXIT', label: 'Exit' },
]

// --- Field configuration ---

const FIELD_CONFIG: FieldConfig[] = [
  {
    label: 'Division',
    key: 'division',
    getValue: plan =>
      plan.hierarchy?.lineage?.find(l => l.level === 'DIVISION')?.name ?? null,
    editable: false,
    type: 'text',
  },
  {
    label: 'Department',
    key: 'department',
    getValue: plan =>
      plan.hierarchy?.lineage?.find(l => l.level === 'DEPARTMENT')?.name ??
      null,
    editable: false,
    type: 'text',
  },
  {
    label: 'Class',
    key: 'class',
    getValue: plan =>
      plan.hierarchy?.lineage?.find(l => l.level === 'CLASS')?.name ?? null,
    editable: false,
    type: 'text',
  },
  {
    label: 'SubClass',
    key: 'subclass',
    getValue: plan =>
      plan.hierarchy?.lineage?.find(l => l.level === 'SUBCLASS')?.name ??
      (plan.hierarchy?.level === 'SUBCLASS' ? plan.hierarchy.name : null),
    editable: false,
    type: 'text',
  },
  {
    label: 'Name',
    key: 'name',
    getValue: plan => plan.name,
    editable: true,
    type: 'text',
  },
  {
    label: 'Season',
    key: 'season',
    getValue: plan => plan.season?.name ?? null,
    editable: false,
    type: 'text',
  },
  {
    label: 'Set/Reset Date',
    key: 'set_reset_date',
    getValue: plan => plan.set_reset_date,
    editable: true,
    type: 'date',
  },
  {
    label: 'Exit Date',
    key: 'exit_date',
    getValue: plan => plan.exit_date,
    editable: true,
    type: 'date',
  },
  {
    label: 'In-Season ST%',
    key: 'planned_st_pct',
    getValue: plan => plan.planned_st_pct,
    editable: true,
    type: 'percent',
  },
  {
    label: 'End of Season ST%',
    key: 'end_of_season_st_pct',
    getValue: plan => plan.end_of_season_st_pct,
    editable: true,
    type: 'percent',
  },
  {
    label: 'Discount Rate',
    key: 'discount_rate',
    getValue: plan => plan.discount_rate,
    editable: true,
    type: 'percent',
  },
  {
    label: 'WOS Goal',
    key: 'target_wos',
    getValue: plan => plan.target_wos,
    editable: true,
    type: 'float',
  },
  {
    label: 'New Store Default %',
    key: 'new_store_default_pct',
    getValue: plan => plan.new_store_default_pct,
    editable: true,
    type: 'percent',
  },
  {
    label: 'Strategic Role',
    key: 'strategic_role',
    getValue: plan => plan.strategic_role,
    editable: true,
    type: 'select',
    options: STRATEGIC_ROLE_OPTIONS,
  },
  {
    label: 'Strategic Intent',
    key: 'strategic_intent',
    getValue: plan => plan.strategic_intent,
    editable: true,
    type: 'select',
    options: STRATEGIC_INTENT_OPTIONS,
  },
  {
    label: 'POG Groups Involved',
    key: 'pog_groups_involved',
    getValue: plan => plan.pog_groups_involved,
    editable: true,
    type: 'text',
  },
  {
    label: 'POGs Involved',
    key: 'pogs_involved',
    getValue: plan => (plan.pogs_involved != null ? plan.pogs_involved : null),
    editable: true,
    type: 'integer',
  },
  {
    label: 'Feature Locations',
    key: 'feature_locations',
    getValue: plan => plan.feature_locations,
    editable: true,
    type: 'text',
  },
]

// --- Shared edit input styles ---

const EDIT_INPUT_CLASS =
  'h-7 border-0 border-b-2 border-blue-500 rounded-none px-1 text-sm font-medium focus-visible:ring-0 focus-visible:ring-offset-0'

const READ_VALUE_CLASS =
  'cursor-pointer text-sm font-medium min-h-[1.75rem] flex items-center px-1'

// --- Helpers ---

function formatDisplayValue(
  value: string | number | null,
  type: FieldType,
  field: FieldConfig
): string {
  if (value == null || value === '') return '-'

  switch (type) {
    case 'date': {
      try {
        return format(parseISO(String(value)), 'MMM d, yyyy')
      } catch {
        return String(value)
      }
    }
    case 'percent':
      return `${value}%`
    case 'select': {
      const selectField = field as SelectFieldConfig
      const option = selectField.options.find(o => o.value === value)
      return option?.label ?? String(value)
    }
    default:
      return String(value)
  }
}

// --- EditableTextField ---

interface EditableTextFieldProps {
  value: string | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (value: string | null) => void
  onCancel: () => void
}

function EditableTextField({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
}: EditableTextFieldProps) {
  const [editValue, setEditValue] = useState(value || '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) setEditValue(value || '')
  }, [value, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = () => {
    const trimmed = editValue.trim()
    if (trimmed !== (value || '')) {
      onSave(trimmed || null)
    } else {
      onCancel()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditValue(value || '')
      onCancel()
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        className={EDIT_INPUT_CLASS}
      />
    )
  }

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {value || '-'}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- EditableNumberField ---

interface EditableNumberFieldProps {
  value: string | number | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (value: number | null) => void
  onCancel: () => void
  step: string
  isPercent?: boolean
}

function EditableNumberField({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
  step,
  isPercent,
}: EditableNumberFieldProps) {
  const [editValue, setEditValue] = useState(value != null ? String(value) : '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isEditing) setEditValue(value != null ? String(value) : '')
  }, [value, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = () => {
    if (editValue.trim() === '') {
      if (value != null) onSave(null)
      else onCancel()
      return
    }
    const parsed =
      step === '1' ? parseInt(editValue, 10) : parseFloat(editValue)
    if (isNaN(parsed)) {
      onCancel()
      return
    }
    if (String(parsed) !== String(value)) {
      onSave(parsed)
    } else {
      onCancel()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setEditValue(value != null ? String(value) : '')
      onCancel()
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type="number"
        step={step}
        value={editValue}
        onChange={e => setEditValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleSave}
        className={EDIT_INPUT_CLASS}
      />
    )
  }

  const displayValue =
    value != null && value !== '' ? `${value}${isPercent ? '%' : ''}` : '-'

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {displayValue}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- EditableSelectField ---

interface EditableSelectFieldProps {
  value: string | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (value: string | null) => void
  onCancel: () => void
  options: SelectOption[]
}

function EditableSelectField({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
  options,
}: EditableSelectFieldProps) {
  const displayLabel = options.find(o => o.value === value)?.label ?? '-'

  if (isEditing) {
    return (
      <Select
        defaultOpen
        value={value ?? undefined}
        onValueChange={newValue => {
          if (newValue !== value) {
            onSave(newValue)
          } else {
            onCancel()
          }
        }}
        onOpenChange={open => {
          if (!open) onCancel()
        }}
      >
        <SelectTrigger className="h-7 border-0 border-b-2 border-blue-500 rounded-none px-1 text-sm font-medium focus:ring-0 focus:ring-offset-0">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {displayLabel}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- EditableDateField ---

interface EditableDateFieldProps {
  value: string | null
  isEditing: boolean
  isSaving?: boolean
  onStartEdit: () => void
  onSave: (value: string | null) => void
  onCancel: () => void
}

function EditableDateField({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onSave,
  onCancel,
}: EditableDateFieldProps) {
  const selectedDate = value ? parseISO(value) : undefined

  const displayValue = value
    ? (() => {
        try {
          return format(parseISO(value), 'MMM d, yyyy')
        } catch {
          return value
        }
      })()
    : '-'

  if (isEditing) {
    return (
      <Popover
        open
        onOpenChange={open => {
          if (!open) onCancel()
        }}
      >
        <PopoverTrigger asChild>
          <p className={READ_VALUE_CLASS}>{displayValue}</p>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={date => {
              if (date) {
                const iso = format(date, 'yyyy-MM-dd')
                onSave(iso)
              } else {
                onSave(null)
              }
            }}
            className="rounded-lg border"
          />
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <p
      onDoubleClick={isSaving ? undefined : onStartEdit}
      className={READ_VALUE_CLASS}
    >
      {displayValue}
      {isSaving && (
        <Loader2 className="ml-1.5 h-3.5 w-3.5 animate-spin text-muted-foreground" />
      )}
    </p>
  )
}

// --- MetricsGrid ---

interface MetricsGridProps {
  plan: ApiPlanDetailResponse
  onSave: (fieldKey: string, value: string | number | null) => void
  savingFields?: Set<string>
}

export function MetricsGrid({ plan, onSave, savingFields }: MetricsGridProps) {
  const [editingField, setEditingField] = useState<string | null>(null)

  const handleSave = (fieldKey: string, value: string | number | null) => {
    setEditingField(null)
    onSave(fieldKey, value)
  }

  const handleCancel = () => setEditingField(null)

  const renderEditor = (field: FieldConfig) => {
    const value = field.getValue(plan)
    const isEditing = editingField === field.key
    const isSaving = savingFields?.has(field.key) ?? false
    const startEdit = () => {
      if (!isSaving) setEditingField(field.key)
    }

    switch (field.type) {
      case 'text':
        return (
          <EditableTextField
            value={value != null ? String(value) : null}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
          />
        )

      case 'float':
        return (
          <EditableNumberField
            value={value}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            step="0.01"
          />
        )

      case 'percent':
        return (
          <EditableNumberField
            value={value}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            step="0.01"
            isPercent
          />
        )

      case 'integer':
        return (
          <EditableNumberField
            value={value}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            step="1"
          />
        )

      case 'select': {
        const selectField = field as SelectFieldConfig
        return (
          <EditableSelectField
            value={value != null ? String(value) : null}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
            options={selectField.options}
          />
        )
      }

      case 'date':
        return (
          <EditableDateField
            value={value != null ? String(value) : null}
            isEditing={isEditing}
            isSaving={isSaving}
            onStartEdit={startEdit}
            onSave={v => handleSave(field.key, v)}
            onCancel={handleCancel}
          />
        )
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-[#101828]">
        Metrics & Attributes
      </h3>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        {FIELD_CONFIG.map(field => {
          const value = field.getValue(plan)

          return (
            <div key={field.key} className="space-y-1">
              <p className="text-xs text-muted-foreground">{field.label}</p>
              {field.editable ? (
                renderEditor(field)
              ) : (
                <p className="text-sm font-medium min-h-[1.75rem] flex items-center px-1 bg-[#f3f4f6] rounded">
                  {formatDisplayValue(value, field.type, field)}
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
